import { InfoModal, AppIcon, AppIcons, UnlockStatus } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';

import { Box, Typography, SxProps, Theme } from '@mui/material';

import { UNLOCK_STATUS_COLOR_TOKENS } from '../trackAssessment.constants';

const TIMELINE_CHART_STRINGS = appStrings.modules.assessMyTrack.timelineChart;
const MODAL_STRINGS = TIMELINE_CHART_STRINGS.profileStrengthModal;

const STATUS_ICON_MAP: Record<UnlockStatus, AppIcons> = {
  on_track: 'Check',
  developing: 'Minus',
  not_yet: 'XClose',
};

const STATUSES_DISPLAY_ORDER: UnlockStatus[] = ['on_track', 'developing', 'not_yet'];

type ProfileStrengthModalProps = Readonly<{
  open: boolean;
  onClose: () => void;
}>;

export const ProfileStrengthModal = ({ open, onClose }: ProfileStrengthModalProps) => {
  return (
    <InfoModal
      open={open}
      onClose={onClose}
      width="xs"
      icon="UserSquare"
      headerContent={
        <Box sx={headerContainerSx}>
          <Typography variant="text-md-semibold" sx={headerTitleSx}>
            {MODAL_STRINGS.title}
          </Typography>
          <Typography variant="text-sm-regular" sx={headerDescriptionSx}>
            {MODAL_STRINGS.description}
          </Typography>
        </Box>
      }
      bodyContent={
        <Box sx={bodyContainerSx}>
          <Box sx={statusListSx}>
            {STATUSES_DISPLAY_ORDER.map((status) => (
              <Box key={status} sx={statusCardSx}>
                <Box sx={statusCardHeaderSx}>
                  <Box sx={statusIconCircleSx(status)}>
                    <AppIcon
                      iconName={STATUS_ICON_MAP[status]}
                      diameter="16px"
                      color="Colors/Base/White"
                      strokeWidth="bold"
                    />
                  </Box>
                  <Typography variant="text-lg-semibold" sx={statusLabelSx}>
                    {TIMELINE_CHART_STRINGS.unlockStatusLabels[status]}
                  </Typography>
                </Box>
                <Typography variant="text-sm-regular" sx={statusDescriptionSx}>
                  {MODAL_STRINGS.statusDescriptions[status]}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      }
    />
  );
};

const headerContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  position: 'relative',
  zIndex: 1,
};

const headerTitleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Gray/900'],
});

const headerDescriptionSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Gray/600'],
});

const bodyContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xl'],
  position: 'relative',
  zIndex: 1,
});

const statusListSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
});

const statusCardSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
  padding: backrs.spacing['spacing-lg'],
  borderRadius: backrs.radius['radius-md'],
  border: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
});

const statusCardHeaderSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: backrs.spacing['spacing-lg'],
});

const statusIconCircleSx =
  (status: UnlockStatus): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 32,
    height: 32,
    minWidth: 32,
    borderRadius: '50%',
    border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
    backgroundColor: backrs.colors[UNLOCK_STATUS_COLOR_TOKENS[status]],
  });

const statusLabelSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Gray/700'],
});

const statusDescriptionSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Gray/600'],
});
